import type {
  AuthorizationAnswer,
  AuthorizationRequest,
  Entities,
  EntityIndex,
  EntityJson,
  EntityUidJson,
  NormalizedPolicy,
  PolicyJson,
  PolicySet,
  PrincipalConstraint,
  ActionConstraint,
  ResourceConstraint,
} from "./cedarTypes";

export interface AuthorizationArgs {
  principal: string;
  principalType: string;
  roles: string[];
  roleType: string;
  policies: PolicyJson[];
  action: string;
  resource?: string;
}

export function isAuthorized({
  principal,
  principalType,
  roles,
  roleType,
  policies,
  action,
  resource,
}: AuthorizationArgs): boolean {
  const entities = entitiesForRequest(principal, principalType, roles, roleType);

  const request = {
    principal: {
      type: principalType,
      id: principal,
    },
    action: {
      type: "Action",
      id: action,
    },
    context: {},
    policies: { staticPolicies: policies },
    entities: entities,
    resource: {
      type: "resource",
      id: resource ?? "1",
    },
  };

  const answer = evaluateAuthorizationRequest(request);
  if (answer.type === "failure") {
    throw new Error(answer.errors.join(", "));
  }

  return answer.type === "success" && answer.response.decision === "allow";
}

function entitiesForRequest(principal: string, principalType: string, roles: string[], roleType: string): Entities {
  const entities: Entities = [];

  entities.push({
    uid: { type: principalType, id: principal },
    attrs: {},
    parents: roles.map((roleId) => ({
      type: roleType,
      id: roleId,
    })),
  });

  for (const roleId of roles) {
    entities.push({
      uid: { type: roleType, id: roleId },
      attrs: {},
      parents: [],
    });
  }

  return entities;
}

function entityKey(uid: EntityUidJson): string {
  return `${uid.type}::${uid.id}`;
}

function normalizePolicySet(policies: PolicySet): NormalizedPolicy[] {
  const normalized: NormalizedPolicy[] = [];

  if (!policies.staticPolicies) {
    return normalized;
  }

  const staticPolicies = policies.staticPolicies;

  const policyArray: Array<{ id: string; policy: PolicyJson }> = staticPolicies.map((p, idx) => ({
    id: `policy${idx}`,
    policy: p,
  }));

  for (const { id, policy } of policyArray) {
    normalized.push({
      id,
      effect: policy.effect,
      principal: policy.principal,
      action: policy.action,
      resource: policy.resource,
    });
  }

  return normalized;
}

function indexEntities(entities: Entities): EntityIndex {
  const entityMap = new Map<string, EntityJson>();
  const ancestors = new Map<string, Set<string>>();

  for (const entity of entities) {
    const key = entityKey(entity.uid);
    entityMap.set(key, entity);
  }

  for (const entity of entities) {
    const key = entityKey(entity.uid);
    const ancestorSet = new Set<string>();

    const queue: EntityUidJson[] = [...entity.parents];
    const visited = new Set<string>();

    while (queue.length > 0) {
      const parent = queue.shift();
      if (!parent) {
        continue;
      }

      const parentKey = entityKey(parent);

      if (visited.has(parentKey)) {
        continue;
      }
      visited.add(parentKey);

      ancestorSet.add(parentKey);

      const parentEntity = entityMap.get(parentKey);
      if (parentEntity) {
        queue.push(...parentEntity.parents);
      }
    }

    ancestors.set(key, ancestorSet);
  }

  return { entities: entityMap, ancestors };
}

function matchesPrincipal(requestUid: EntityUidJson, constraint: PrincipalConstraint, index: EntityIndex): boolean {
  if (constraint.op === "All") {
    return true;
  } else if (constraint.op === "==") {
    return entityKey(requestUid) === entityKey(constraint.entity);
  } else if (constraint.op === "in") {
    const targetKey = entityKey(constraint.entity);
    const reqKey = entityKey(requestUid);

    if (reqKey === targetKey) {
      return true;
    }

    const ancestorSet = index.ancestors.get(reqKey);
    return ancestorSet?.has(targetKey) === true;
  } else {
    throw new Error(`Invalid principal constraint: ${JSON.stringify(constraint)}`);
  }
}

function matchesAction(requestAction: EntityUidJson, constraint: ActionConstraint): boolean {
  switch (constraint.op) {
    case "All":
      return true;
    case "in":
      return constraint.entities.some((entity) => entityKey(entity) === entityKey(requestAction));
    case "==":
      return entityKey(requestAction) === entityKey(constraint.entity);
    default:
      throw new Error(`Invalid action constraint: ${JSON.stringify(constraint)}`);
  }
}

function matchesResource(requestResourceId: string, constraint: ResourceConstraint | undefined): boolean {
  if (!constraint || constraint.op === "All") {
    return true;
  }
  return constraint.ids.includes(requestResourceId);
}

function policyApplies(policy: NormalizedPolicy, request: AuthorizationRequest, index: EntityIndex): boolean {
  const principalMatch = matchesPrincipal(request.principal, policy.principal, index);
  const actionMatch = matchesAction(request.action, policy.action);
  const resourceMatch = matchesResource(request.resource.id, policy.resource);

  return principalMatch && actionMatch && resourceMatch;
}

function evaluateAuthorizationRequest(request: AuthorizationRequest): AuthorizationAnswer {
  const index = indexEntities(request.entities);
  const normalizedPolicies = normalizePolicySet(request.policies);
  const applicable = normalizedPolicies.filter((p) => policyApplies(p, request, index));
  const hasForbid = applicable.some((p) => p.effect === "forbid");
  const hasPermit = applicable.some((p) => p.effect === "permit");
  const decision = hasPermit && !hasForbid ? "allow" : "deny";

  const reason = applicable.map((p) => p.id).join(", ");

  return {
    type: "success",
    response: {
      decision,
      reason,
    },
  };
}
